// Function index:
//   mountLedgerPanel(partyId) — wire the Ledger button + overlay (people/
//       factions/places) once per party session (like mountHexHistoryOverlay).
//       Refetches on every open since NPC attitude, faction standing, and
//       place lore all drift during play; read-only.

import { getLedger, type LedgerPerson, type LedgerPlace, type FactionStanding } from '../api.js';

export function mountLedgerPanel(partyId: number): void {
  const btn = document.getElementById('ledger-btn') as HTMLButtonElement;
  const overlay = document.getElementById('ledger-overlay') as HTMLDivElement;
  const close = document.getElementById('ledger-close') as HTMLButtonElement;
  const error = document.getElementById('ledger-error') as HTMLDivElement;
  const peopleHost = document.getElementById('ledger-people') as HTMLDivElement;
  const factionsHost = document.getElementById('ledger-factions') as HTMLDivElement;
  const placesHost = document.getElementById('ledger-places') as HTMLDivElement;

  async function load(): Promise<void> {
    error.hidden = true;
    for (const host of [peopleHost, factionsHost, placesHost]) host.innerHTML = '<div class="ledger-empty">loading…</div>';
    try {
      const ledger = await getLedger(partyId);
      renderPeople(peopleHost, ledger.people);
      renderFactions(factionsHost, ledger.factions);
      renderPlaces(placesHost, ledger.places);
    } catch (err) {
      error.textContent = `Could not load the ledger: ${(err as Error).message}`;
      error.hidden = false;
      for (const host of [peopleHost, factionsHost, placesHost]) host.innerHTML = '';
    }
  }

  btn.addEventListener('click', () => {
    overlay.hidden = false;
    void load();
  });
  const hide = (): void => { overlay.hidden = true; };
  close.addEventListener('click', hide);
  overlay.addEventListener('click', (e) => { if (e.target === overlay) hide(); });
  window.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && !overlay.hidden) hide();
  });
}

// Why: precedent thresholds — hostile/wary checked before allied/friendly so
// the wide neutral band in the middle falls through last.
function attitudeLabel(a: number): string {
  if (a <= -50) return 'hostile';
  if (a <= -15) return 'wary';
  if (a >= 50) return 'allied';
  if (a >= 15) return 'friendly';
  return 'neutral';
}

function renderPeople(host: HTMLElement, people: LedgerPerson[]): void {
  if (people.length === 0) {
    host.innerHTML = '<div class="ledger-empty">No one knows you yet…</div>';
    return;
  }
  host.innerHTML = people.map((p) => `
    <div class="inv-row">
      <span class="nm">${esc(p.name)}</span>
      <span class="qty">${attitudeLabel(p.attitude)}</span>
      <span class="kind">${esc(p.archetype)}${p.alive ? '' : ' · deceased'}</span>
    </div>
    ${p.summary ? `<p class="ledger-summary">${esc(p.summary)}</p>` : ''}
    ${p.moments.length > 0 ? `<ul class="ledger-moments">${p.moments.map((m) => `<li>${esc(m)}</li>`).join('')}</ul>` : ''}
  `).join('');
}

function renderFactions(host: HTMLElement, factions: FactionStanding[]): void {
  if (factions.length === 0) {
    host.innerHTML = '<div class="ledger-empty">No factions have noticed you yet…</div>';
    return;
  }
  host.innerHTML = factions.map((f) => {
    const sign = f.standing >= 0 ? '+' : '';
    return `<div class="inv-row">
      <span class="nm">${esc(f.name)}</span>
      <span class="qty">${esc(f.label)}</span>
      <span class="kind">${sign}${f.standing}</span>
    </div>`;
  }).join('');
}

function renderPlaces(host: HTMLElement, places: LedgerPlace[]): void {
  if (places.length === 0) {
    host.innerHTML = '<div class="ledger-empty">Nowhere remembers you yet…</div>';
    return;
  }
  host.innerHTML = places.map((p) => `
    <div class="inv-row">
      <span class="nm">${esc(p.hex_name)}</span>
      <span class="kind">${esc(p.world_name)} · ${esc(p.kind)}</span>
    </div>
    ${p.texts.map((t) => `<p class="ledger-summary">${esc(t)}</p>`).join('')}
  `).join('');
}

function esc(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));
}
